import { Box, Skeleton, TablePagination, useTheme } from "@mui/material";
import * as React from "react";
import { useLocation } from "react-router";

import { DatasetFileGrid } from "@/shared/components/DatasetFileGrid";
import { type FileRecord } from "@/shared/domain/files";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import { FileSystemNode } from "@/shared/domain/files/filesystem";

import { assetFileViewOverride } from "./assetFileViewOverride";
import { FileBrowserTable } from "./FileBrowserTable";
import { NoFilesUploadedAlert } from "./NoFilesUploadedAlert";
import { PaginationConfig } from "./state";

interface FileBrowserDisplaysProps {
  displayMode: string;
  associationId: string;
  tableConfig: PaginationConfig;
  /**
   * Undefined while the count loads, and for an org, device or device type
   * folder with no search term, which the service cannot count. The pager then
   * reads "of more than N".
   */
  totalItemCount: number | undefined;
  /** Whether the directory listing returned a next page token. */
  hasNextPage: boolean;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => void;
  onRowsPerPageChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | undefined,
  ) => void;
  currentPage: FileSystemNode[] | undefined;
  isFetching: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
  currentPageFetchedAt: number;
  isSearching: boolean;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  gridItems: number;
  selectedItems: FileSystemNode[];
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  /**
   * Opens the ingest wizard for a robot description. Owned by the browser.
   *
   * Optional — absent when `release-3d-assets` is off, and callers pass it
   * straight through, so the components that act on it handle its absence.
   */
  onIngestAsAsset?: (file: FileRecord) => void;
  /**
   * Whether files show their ingestion labels (`release-ingestion-labels`),
   * decided by the browser. Absent means no labels.
   */
  showIngestionLabels?: boolean;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn, direction: SortDirection) => void;
}

export const FileBrowserDisplays: React.FC<FileBrowserDisplaysProps> = ({
  displayMode,
  associationId,
  tableConfig,
  totalItemCount,
  hasNextPage,
  onPageChange,
  onRowsPerPageChange,
  currentPage,
  isFetching,
  isPending,
  isPlaceholderData,
  currentPageFetchedAt,
  isSearching,
  handleDeleteRow,
  handleRowDoubleClick,
  gridItems,
  selectedItems,
  handleActionConfirmation,
  onIngestAsAsset,
  showIngestionLabels = false,
  handleSelectRows,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  const theme = useTheme();

  // The grid knows nothing about ingestable sources, so the file browser passes
  // it an override that opens them in the ingest wizard instead of navigating
  // to them. The table's rows and menu belong to the file browser and call
  // assetSourceToIngest themselves.
  const fileViewOverride = React.useMemo(
    () => assetFileViewOverride(onIngestAsAsset),
    [onIngestAsAsset],
  );

  const { pathname } = useLocation();
  const cleanPathname = pathname.replace(/^\/+|\/+$/g, "");
  const inFolder = cleanPathname.split("/").length > 2;

  let content;
  if ((currentPage && currentPage.length > 0) || isSearching || inFolder) {
    content = (
      <>
        {displayMode === "table" ? (
          <FileBrowserTable
            currentPage={currentPage ?? []}
            associationId={associationId}
            handleDeleteRow={handleDeleteRow}
            handleRowDoubleClick={handleRowDoubleClick}
            handleActionConfirmation={handleActionConfirmation}
            onIngestAsAsset={onIngestAsAsset}
            showIngestionLabels={showIngestionLabels}
            isFetching={isFetching}
            isPlaceholderData={isPlaceholderData}
            currentPageFetchedAt={currentPageFetchedAt}
            isSearching={isSearching}
            selectedItems={selectedItems}
            handleSelectRows={handleSelectRows}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
          />
        ) : (
          <DatasetFileGrid
            associationId={associationId}
            currentPage={currentPage ?? []}
            handleRowClick={handleRowDoubleClick}
            isSearching={isSearching}
            gridItems={gridItems}
            selectedItems={selectedItems}
            handleSelectRows={handleSelectRows}
            fileViewOverride={fileViewOverride}
            emptyContent={
              <NoFilesUploadedAlert associationId={associationId} />
            }
          />
        )}
        <TablePagination
          component={Box}
          // Without a total from the server, the last page reveals it.
          count={
            totalItemCount ??
            (hasNextPage
              ? -1
              : tableConfig.page * tableConfig.rowsPerPage +
                (currentPage?.length ?? 0))
          }
          page={tableConfig.page}
          rowsPerPage={tableConfig.rowsPerPage}
          labelRowsPerPage={"Items per page:"}
          rowsPerPageOptions={[10, 25, 50]}
          // With an unknown total (count -1), MUI never disables the next
          // button, so it is disabled when the listing has no next page.
          nextIconButtonProps={
            totalItemCount === undefined
              ? { disabled: !hasNextPage }
              : undefined
          }
          onPageChange={(event, newPage) => {
            onPageChange(event, newPage);
          }}
          onRowsPerPageChange={(event) => {
            onRowsPerPageChange(event);
          }}
        />
      </>
    );
  } else {
    content = <NoFilesUploadedAlert associationId={associationId} />;
  }

  if (isPending) {
    return (
      <Box sx={{ width: "100%", marginBottom: theme.spacing(1) }}>
        <Skeleton animation="wave" variant="rounded" height={150} />
      </Box>
    );
  }

  return content;
};
